<template>
  <div>
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="border-0 shadow-sm rounded-4">
          <template v-slot:header>
            <div class="d-flex align-items-center justify-content-between p-3">
              <div class="d-flex align-items-center">
                <div class="icon-circle bg-primary text-white me-3">
                  <vue-feather type="file-text" size="20"></vue-feather>
                </div>
                <div>
                  <h4 class="mb-0 fw-bold text-primary">Employee Payrolls Managment</h4>
                  <p class="text-muted mb-0 small">Manage and view all employee payrolls</p>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <button v-if="profile.role !== 'user'" class="btn btn-primary btn-sm rounded-pill me-3" @click="payrollModal = true">
                  <vue-feather type="plus" size="16" class="me-1"></vue-feather>
                  Add Payroll
                </button>
                <UInput v-model="dataTableSearch" size="md" placeholder="Search payroll" class="mb-0 shadow-sm" style="width: 250px;"/>
              </div>
            </div>
          </template>

          <!-- Summary Cards -->
          <div v-if="!loading" class="row p-3 g-3 mb-3">
            <div class="col-md-3">
              <div class="stat-card bg-light rounded-4 p-3 h-100 border">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0 text-primary fw-bold">Total Payrolls</h6>
                  <div class="icon-circle-sm bg-primary text-white">
                    <vue-feather type="file-text" size="14"></vue-feather>
                  </div>
                </div>
                <h3 class="mb-0 fw-bold">{{ payrollData.length }}</h3>
                <p class="text-muted mb-0 small">All payrolls in the system</p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="stat-card bg-light rounded-4 p-3 h-100 border">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0 text-success fw-bold">Confirmed</h6>
                  <div class="icon-circle-sm bg-success text-white">
                    <vue-feather type="check-circle" size="14"></vue-feather>
                  </div>
                </div>
                <h3 class="mb-0 fw-bold">{{ getStatusCount('confirmed') }}</h3>
                <p class="text-muted mb-0 small">Confirmed payrolls</p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="stat-card bg-light rounded-4 p-3 h-100 border">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0 text-danger fw-bold">Draft</h6>
                  <div class="icon-circle-sm bg-danger text-white">
                    <vue-feather type="edit" size="14"></vue-feather>
                  </div>
                </div>
                <h3 class="mb-0 fw-bold">{{ getStatusCount('draft') }}</h3>
                <p class="text-muted mb-0 small">Draft payrolls</p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="stat-card bg-light rounded-4 p-3 h-100 border">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0 text-info fw-bold">Current Month</h6>
                  <div class="icon-circle-sm bg-info text-white">
                    <vue-feather type="calendar" size="14"></vue-feather>
                  </div>
                </div>
                <h3 class="mb-0 fw-bold">{{ getCurrentMonthCount() }}</h3>
                <p class="text-muted mb-0 small">Payrolls for {{ getCurrentMonth() }}</p>
              </div>
            </div>
          </div>

          <div v-if="loading" class="text-center py-5">
            <Spinner spinner-class="mb-1" />
            <p class="text-muted">Loading payroll data...</p>
          </div>

          <div v-if="!loading" class="px-3">
            <DataTable show-filter-buttons :data="payrollData" :headers="dataTableHeaders"
                        :search-keyword="dataTableSearch" :per-page="dataTablePerPage" :current-page="dataTablePage">
              <template v-slot:default="{row}">
                <Cell v-for="(cellData, key) in row">
                  <Badge v-if="key.toString().toLowerCase() === 'status' || key === 5" pill :type="getPillByStatus(cellData)" class="badge-lg">
                    <vue-feather :type="cellData === 'draft' ? 'edit' : 'check-circle'" size="14" class="me-1"></vue-feather>
                    {{cellData}}
                  </Badge>
                  <span v-else-if="key === 0">{{cellData.split("-").pop()}}</span>
                  <span v-else>{{cellData}}</span>
                </Cell>
                <Cell>
                  <Button class="btn-primary btn-sm rounded-pill" @click="payrollDetails(row[0])">
                    <vue-feather type="eye" size="14" class="me-1"></vue-feather>
                    View
                  </Button>
                </Cell>
              </template>
            </DataTable>
          </div>

          <div v-if="!loading" class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between border-top mt-3">
            <button class="btn btn-outline-dark rounded-pill text-uppercase me-4" @click="$router.go(-1)">
              <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
              Back
            </button>
            <div class="me-sm-4 mb-sm-0 mb-3">
              <p class="mb-2 small">Rows per page</p>
              <select data="" class="form-select shadow-sm" v-model="dataTablePerPage">
                <option v-for="item in [5, 10, 25, 50, 100]" :key="item" :value="item">{{item}}</option>
              </select>
            </div>
            <div>
              <Pagination v-model="dataTablePage" :pages="Math.ceil(dataTable.length/dataTablePerPage)">
                <template v-slot:next>
                  <vue-feather type="chevron-right" size="16"></vue-feather>
                </template>
                <template v-slot:previous>
                  <vue-feather type="chevron-left" size="16"></vue-feather>
                </template>
              </Pagination>
            </div>
          </div>
        </Card>
      </div>
    </div>

    <!-- Add Payroll Modal -->
    <Modal :open="payrollModal" @closed="payrollModal = false" dialog-size="modal-md">
      <div class="modal-header bg-light">
        <div class="d-flex align-items-center">
          <div class="icon-circle-sm bg-primary text-white me-2">
            <vue-feather type="plus" size="16"></vue-feather>
          </div>
          <h5 class="modal-title m-0 fw-bold">Create New Payroll</h5>
        </div>
        <button @click="payrollModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body p-4">
        <p class="text-muted mb-4">Create a new payroll for the selected month and year. The payroll code will be generated automatically.</p>
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="form-group col-md-12">
              <label for="code" class="form-label fw-medium">Payroll Code:</label>
              <div class="input-group">
                <span class="input-group-text bg-light">
                  <vue-feather type="hash" size="16"></vue-feather>
                </span>
                <input type="text" class="form-control shadow-sm" v-model="payroll.code" readonly>
              </div>
              <small class="text-muted">Automatically generated code</small>
            </div>
            <div class="form-group col-md-6">
              <label for="month" class="form-label fw-medium">Month:</label>
              <div class="input-group">
                <span class="input-group-text bg-light">
                  <vue-feather type="calendar" size="16"></vue-feather>
                </span>
                <select data="" class="form-select shadow-sm" v-model="payroll.month">
                  <option :value="payroll.month" disabled selected>{{ payroll.month }}</option>
                  <option value="January">January</option>
                  <option value="February">February</option>
                  <option value="March">March</option>
                  <option value="April">April</option>
                  <option value="May">May</option>
                  <option value="June">June</option>
                  <option value="July">July</option>
                  <option value="August">August</option>
                  <option value="September">September</option>
                  <option value="October">October</option>
                  <option value="November">November</option>
                  <option value="December">December</option>
                </select>
              </div>
            </div>
            <div class="form-group col-md-6">
              <label for="year" class="form-label fw-medium">Year:</label>
              <div class="input-group">
                <span class="input-group-text bg-light">
                  <vue-feather type="calendar" size="16"></vue-feather>
                </span>
                <select data="" class="form-select shadow-sm" v-model="payroll.year">
                  <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary rounded-pill" data-dismiss="modal" @click="payrollModal = false">
          <vue-feather type="x" size="16" class="me-1"></vue-feather>
          Cancel
        </button>
        <button v-if="profile.role !== 'user'" type="button" class="btn btn-primary rounded-pill" @click="addPayroll">
          <vue-feather type="save" size="16" class="me-1"></vue-feather>
          Create Payroll
        </button>
      </div>
    </Modal>
    <!-- Add Payroll Modal End -->

  </div>
</template>

<script>
import payrollService from "@/api/payrollService";
import formatDate from "@/utils/formatDate";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import loadingButton from "@/utils/loadingButton";
import VueFeather from 'vue-feather';

export default {
  name: "Payroll",
  components: {
    VueFeather
  },

  data(){
    return {
      profile: {},
      dataTableHeaders: [
        '#',
        'Payroll No',
        'Run By',
        'Month',
        'Year',
        'Status',
        'Created',
        null
      ],
      loading: true,
      dataTable: [],
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
      payrollModal: false,
      payroll: {
        code: '',
        month: '',
        year: '2025',
      },
      nextNo: {},
    }
  },

  methods: {
    payrollDetails(id) {
      router.push('/payroll/' + id)
    },
    async getPayrolls() {
      this.dataTable = await payrollService.getPayrolls()
      this.nextNo = await payrollService.getPayrollNextNo()
      this.payroll.code = this.nextNo.next
      this.loading = false
    },
    async addPayroll(e) {
      loadingButton(e)
      let response = await payrollService.createPayroll(this.payroll)
      if (response) {
        this.payrollModal = false
        await this.getPayrolls()
      }
      loadingButton(e, false)
    },
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'draft':
          return 'danger';
        case 'confirmed':
          return 'success';
        default:
          return 'primary';
      }
    },
    getPayrollDuration() {
      let nowTime = new Date()
      let lastTime = new Date(nowTime.getFullYear(), nowTime.getMonth() - 1)
      let month = nowTime.getDate() > 8 ? nowTime : lastTime
      this.payroll.month = month.toLocaleString('default', { month: 'long' })
      // this.payroll.year = month.getFullYear()
    },
    // New methods for summary cards
    getStatusCount(status) {
      return this.dataTable.filter(payroll => payroll.status === status).length;
    },
    getCurrentMonth() {
      const now = new Date();
      return now.toLocaleString('default', { month: 'long' });
    },
    getCurrentMonthCount() {
      const currentMonth = this.getCurrentMonth();
      return this.dataTable.filter(payroll => payroll.month === currentMonth).length;
    }
  },

  computed: {
    payrollData(){
      let data = [];
      for (let i = 0; i < this.dataTable.length; i++) {
        let fields = [
          this.dataTable[i].id,
          this.dataTable[i].code,
          this.dataTable[i].user.first_name + ' ' + this.dataTable[i].user.last_name,
          this.dataTable[i].month,
          this.dataTable[i].year,
          this.dataTable[i].status,
          formatDate(this.dataTable[i].created_at)
        ];
        data.push(fields);
      }
      return data;
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const previousYear = currentYear - 1;
      return [previousYear, currentYear];
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.getPayrollDuration()
    await this.getPayrolls()
    if(this.$route.query.add)
      this.payrollModal = true
  },

}
</script>

<style scoped>
/* Icon styles */
.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle-sm {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Card styles */
.stat-card {
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

/* Badge styles */
.badge-lg {
  font-size: 0.85rem;
  padding: 0.35rem 0.65rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  white-space: nowrap;
}

.table td {
  vertical-align: middle;
}

/* Button hover effects */
.btn-primary.rounded-pill:hover,
.btn-outline-dark.rounded-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stat-card {
    margin-bottom: 1rem;
  }
}
</style>
